// Permission keys. Roles hold lists of these; routes and pages declare which one they need.
// Keys are stored in the database: add new ones freely, never rename existing ones.
export interface PermissionDef {
  key: string
  label: string
}
export interface PermissionGroup {
  id: string
  label: string
  perms: PermissionDef[]
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'admin',
    label: 'Admin area',
    perms: [
      { key: 'admin.access', label: 'Use the Briary admin area' },
      { key: 'users.view', label: 'See users and their access' },
      { key: 'users.manage', label: 'Add, change and disable users' },
      { key: 'roles.manage', label: 'Change roles and permissions' },
      { key: 'audit.view', label: 'See the audit log' },
      { key: 'emails.view', label: 'Read the full outgoing email log and attachments' },
      { key: 'settings.manage', label: 'Change system settings' },
      { key: 'legacy.import', label: 'Run imports from the old hub' },
    ],
  },
  {
    id: 'projects',
    label: 'Companies, projects and plots',
    perms: [
      { key: 'projects.view', label: 'See companies, projects and plots' },
      { key: 'projects.edit', label: 'Add and change companies, projects and plots' },
      { key: 'projects.delete', label: 'Archive or delete projects and plots' },
      { key: 'files.manage', label: 'Upload and remove documents' },
      { key: 'access.manage', label: 'Give client managers access to projects' },
    ],
  },
  {
    id: 'photos',
    label: 'Site photos and evidence',
    perms: [
      { key: 'photos.view', label: 'See site photos' },
      { key: 'photos.review', label: 'Approve and reject photos and photo reports' },
    ],
  },
  {
    id: 'sap',
    label: 'Kaizen SAP',
    perms: [
      { key: 'sap.view', label: 'See SAP projects and worksheets' },
      { key: 'sap.edit', label: 'Edit SAP inputs and recalculate' },
      { key: 'sap.tokens', label: 'Manage Kaizen desktop API tokens' },
      { key: 'packs.manage', label: 'Preview, issue and withdraw project packs' },
    ],
  },
  {
    id: 'epc',
    label: 'EPC, BREL and lodgement',
    perms: [
      { key: 'epc.view', label: 'See EPC requests and lodgements' },
      { key: 'epc.manage', label: 'Process EPC requests and BRELs' },
      { key: 'epc.lodge', label: 'Lodge EPCs' },
    ],
  },
  {
    id: 'airtests',
    label: 'Air testing',
    perms: [
      { key: 'airtests.view', label: 'See air test bookings' },
      { key: 'airtests.manage', label: 'Book and manage air tests' },
    ],
  },
  {
    id: 'sales',
    label: 'Enquiries and quotes',
    perms: [
      { key: 'enquiries.view', label: 'See enquiries' },
      { key: 'enquiries.manage', label: 'Handle enquiries' },
      { key: 'quotes.view', label: 'See quotes' },
      { key: 'quotes.manage', label: 'Create and send quotes' },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    perms: [
      { key: 'invoices.view', label: 'See sales invoices, credit notes and debtors' },
      { key: 'invoices.manage', label: 'Create, send and credit invoices' },
      { key: 'bills.view', label: 'See supplier bills and their files' },
      { key: 'bills.manage', label: 'Change supplier bill company, project, invoice and plot links' },
      { key: 'bank.manage', label: 'Allocate bank transactions' },
      { key: 'purchaseorders.view', label: 'See purchase orders' },
      { key: 'purchaseorders.manage', label: 'Manage purchase orders' },
      { key: 'xero.manage', label: 'Connect and sync Xero' },
    ],
  },
  {
    id: 'ops',
    label: 'Operations',
    perms: [
      { key: 'tasks.view', label: 'See tasks' },
      { key: 'tasks.create', label: 'Create and assign new tasks' },
      { key: 'tasks.manage', label: 'Assign and manage tasks' },
      { key: 'nhbc.manage', label: 'Manage NHBC uploads' },
      { key: 'feedback.manage', label: 'Handle client feedback and site notification banners' },
      { key: 'chat.use', label: 'Use staff chat and TeamChat' },
      { key: 'assist.manage', label: 'Manage the Briary Assist knowledge bank and device keys' },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    perms: [
      { key: 'hr.self', label: 'Use HR self-service (holiday, sickness, expenses)' },
      { key: 'hr.manage', label: 'Manage staff, leave and HR records' },
      { key: 'training.view', label: 'Watch training courses and take exams' },
      { key: 'training.manage', label: 'Manage training courses, plans and results' },
      { key: 'garments.manage', label: 'Manage uniform (garment) orders' },
      { key: 'healthsafety.manage', label: 'Manage health & safety documents for client managers' },
    ],
  },
  {
    id: 'models',
    label: 'Drawings & models',
    perms: [
      { key: 'models.view', label: 'See drawings, SketchUp models and their plot matches' },
      { key: 'models.manage', label: 'Upload drawings for the model builder and choose models for plots' },
    ],
  },
  {
    // Not in STAFF_DEFAULT_PERMISSIONS: a super admin grants these through roles.
    id: 'siteplans',
    label: 'Site plans',
    perms: [
      { key: 'siteplans.view', label: 'Open site plans (read-only)' },
      { key: 'siteplans.edit', label: 'Create, analyse and edit site plans' },
      { key: 'siteplans.apply', label: 'Write site plan values into the hub’s plot records' },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    perms: [
      { key: 'wlc.use', label: 'Use Whole Life Carbon' },
      { key: 'grid.use', label: 'Use Grid Capacity' },
      { key: 'statements.use', label: 'Use Energy & sustainability statements' },
      { key: 'statements.policies', label: 'Edit the planning policy library (accept or reject policy requirements)' },
      { key: 'psi.use', label: 'Use Kaizen PSI (thermal bridge Ψ and fRsi calculations)' },
      { key: 'psi.3d', label: 'Use Kaizen PSI 3-D (ground floors and other 3-D junctions; also needs Kaizen PSI)' },
    ],
  },
]

export const ALL_PERMISSIONS: string[] = PERMISSION_GROUPS.flatMap((g) => g.perms.map((p) => p.key))

/** Independent gates. Explicit bill/PO management implies reading that module.
 * Adding catalog keys never assigns them to a role. Bill management edits links
 * only; it does not create/delete bills or change their financial/provider data. */
export const BILL_READ_PERMISSIONS = ['bills.view', 'bills.manage'] as const
export const PURCHASE_ORDER_READ_PERMISSIONS = ['purchaseorders.view', 'purchaseorders.manage'] as const
export type PermissionRequirement = string | readonly string[]
export const matchesPermission = (permissions: readonly string[], required: PermissionRequirement): boolean =>
  typeof required === 'string' ? permissions.includes(required) : required.some(p => permissions.includes(p))

/** Shared sales document reads fail closed for purchase/unknown kinds. */
export const SALES_INVOICE_KINDS = ['invoice', 'credit'] as const
export const isSalesInvoiceKind = (kind: unknown): kind is typeof SALES_INVOICE_KINDS[number] => kind === 'invoice' || kind === 'credit'

/** What the standard staff role can do (most of the office). Office admins and system admins add more. */
export const STAFF_DEFAULT_PERMISSIONS = [
  'admin.access',
  'projects.view',
  'projects.edit',
  'files.manage',
  'photos.view',
  'photos.review',
  'sap.view',
  'sap.edit',
  'packs.manage',
  'epc.view',
  'epc.manage',
  'airtests.view',
  'airtests.manage',
  'models.view',
  'models.manage',
  'enquiries.view',
  'enquiries.manage',
  'quotes.view',
  'tasks.view',
  'tasks.create',
  'hr.self',
  'chat.use',
  'training.view',
  'wlc.use',
  'grid.use',
  'statements.use',
  'psi.use',
]
